import ElakayImage from '../../../components/images/ElakayImage';
import { resolveProductShop } from "../../shops/productShop";
import React from "react";
import { toImageUrl } from "../../../config";
import { getCardGlobalPresentation } from "../../../utils/appearanceSettings";
import { DropshippingLanguageContext, dropshippingText, useDropshippingText } from "../i18n";
import { Link } from "react-router-dom";
import { MdAddBusiness } from "react-icons/md";
import { FaBoxes, FaCheck } from "react-icons/fa";
import { providerStorefrontPath } from "../providerWorkspace";
import { providerShopDisplayName, providerShopMonogram } from "../utils/providerShopIdentity";
import ProviderBadge from "./ProviderBadge";
import GlobalRatingBadge from "./GlobalRatingBadge";
import { countryOfOrigin } from "../utils/countryOfOrigin";
import { elakayAutoText, elakayPluralText } from "../../pageInside/dataElakay";
import "../../pageInside/Card.css";
import "./ProviderProductCard.css";

// The source product has no Seller filterProduct/cart data. Reuse Elakay's
// card presentation and appearance settings while keeping its actions explicit.
// variant="manage" (default, unchanged): the actions row used by My Provider Products.
// variant="browse": the light Buyer-style card used across Home / Catalog / Search / Shop --
// the whole card opens the product preview; only badges, price and availability are shown.
// variant="history": the Best Sellers-style card of Recently Viewed Provider Products (Swiper AND
// full page): big image, stars, 2-line name, price, Provider identity + ONE Provider action.
const ProviderProductCard = ({ variant = "manage", showProvider = true, ...props }) => {
  if (variant === "browse") return <BrowseCard product={props.product} linked={props.linked} showProvider={showProvider} to={props.to} />;
  if (variant === "history") return <HistoryCard product={props.product} />;
  return <ManageCard {...props} />;
};

const ManageCard = ({ product, linked = false, onAddProduct, onManage, onViewProduct, onBuyWholesale }) => {
  const t = useDropshippingText();
  const infoSource = React.useContext(DropshippingLanguageContext);
  const tw = dropshippingText(infoSource, "providerProductWizard"); // namespace complet (5 langues)
  const presentation = getCardGlobalPresentation(infoSource?.allSettings);
  const wholesaleOnly = product.dropshippingEnabled === false;
  // Actions conditionnelles : Buy Wholesale seulement si le Provider active le gros ;
  // Add / Added selon dropshipping -- les deux peuvent coexister.
  const canBuyWholesale = Boolean(product.wholesaleEnabled);
  const added = linked || Boolean(product.sellerLink && product.sellerLink.status !== "removed");
  return (
    <div className={`product-card elakay-grid-card provider-product-card ${presentation.className || ""}`} style={{ ...presentation.style, ...presentation.variables }}>
      <ProviderBadge label={t("provider")} />
      {product.images?.[0] ? <ElakayImage preset="product-card" src={toImageUrl(product.images[0])} alt={product.name || ""} loading="lazy" /> : <div className="provider-product-card__no-image" aria-hidden="true" />}
      <h3>{product.name}</h3>
      <p className="provider-product-card__base-price">{t("providerPrice")}: {Number(product.basePrice).toLocaleString(undefined, { style: "currency", currency: "USD" })}</p>
      {countryOfOrigin(product.countryOfOrigin) && (
        <p className="provider-product-card__origin">{elakayAutoText("h_ppc_made_in", "Made in {value0}", { value0: countryOfOrigin(product.countryOfOrigin).name })} {countryOfOrigin(product.countryOfOrigin).flag}</p>
      )}
      {product.minimumOrderQuantity > 1 && <p className="provider-product-card__moq">{t("minimumOrder", { count: product.minimumOrderQuantity })}</p>}
      {Number(product.stock) > 0 && <p className="provider-product-card__stock">{t("stockAvailable", { count: product.stock })}</p>}
      <div className="provider-product-card__actions">
        {onViewProduct && <button type="button" className="provider-product-card__view-btn" onClick={() => onViewProduct(product)}>{t("viewProduct")}</button>}
        {canBuyWholesale && (
          <button type="button" className="provider-product-card__wholesale-btn" onClick={() => (onBuyWholesale || onViewProduct)?.(product)}>{tw("t175")}</button>
        )}
        {added ? (
          <button type="button" className="provider-product-card__added-btn" onClick={() => onManage?.(product)} aria-label={t("manageAdded", { name: product.name })}>{t("added")}</button>
        ) : (
          <button type="button" className="provider-product-card__add-btn" disabled={wholesaleOnly} onClick={() => onAddProduct?.(product)} aria-label={t("addNamed", { name: product.name })}>{t(wholesaleOnly ? "wholesaleOnly" : "addProduct")}</button>
        )}
      </div>
    </div>
  );
};
export const moneyOf = (v, currency = "USD") => {
  try { return (Number(v) || 0).toLocaleString("en-US", { style: "currency", currency }); } catch { return `$${(Number(v) || 0).toFixed(2)}`; }
};

// `to` (optional) overrides the destination; default: the professional product page.
const BrowseCard = ({ product, linked, showProvider, to }) => {
  const infoSource = React.useContext(DropshippingLanguageContext);
  const presentation = getCardGlobalPresentation(infoSource?.allSettings);
  const [broken, setBroken] = React.useState(false);
  const inShop = linked || Boolean(product.sellerLink && product.sellerLink.status !== "removed");
  const soldOut = Number(product.stock) <= 0;
  const tiers = (product.quantityPricing || []).length;
  const moq = Math.max(1, Number(product.minimumOrderQuantity) || 1);
  return (
    <Link to={to || `/seller/provider-products/${product._id}`} className={`product-card elakay-grid-card provider-product-card provider-product-card--browse ${presentation.className || ""}`} style={{ ...presentation.style, ...presentation.variables }} aria-label={product.name}>
      <span className="ppc__media">
        {product.images?.[0] && !broken ? <ElakayImage preset="product-card" src={toImageUrl(product.images[0])} alt="" loading="lazy" onError={() => setBroken(true)} /> : <span className="provider-product-card__no-image" aria-hidden="true" />}
        <span className="ppc__badges">
          {product.wholesaleEnabled && <span className="ppc__badge ppc__badge--wholesale">{elakayAutoText("h_ppc_wholesale", "Wholesale")}</span>}
          {product.dropshippingEnabled && <span className="ppc__badge ppc__badge--drop">{elakayAutoText("h_ppc_drop", "Dropshipping")}</span>}
          {product.paEnabled && <span className="ppc__badge ppc__badge--pa">PA</span>}
        </span>
        {inShop && <span className="ppc__inshop">{elakayAutoText("h_ppc_inshop", "In My Shop")}</span>}
      </span>
      <span className="ppc__body">
        <span className="ppc__name">{product.name}</span>
        {showProvider && product.shop?.shopName && <span className="ppc__provider">{product.shop.shopName}</span>}
        <GlobalRatingBadge ratings={product.ratings} size="13px" />
        <span className="ppc__price">{moneyOf(product.basePrice, product.currency)}</span>
        {countryOfOrigin(product.countryOfOrigin) && (
          <span className="ppc__meta">{elakayAutoText("h_ppc_made_in", "Made in {value0}", { value0: countryOfOrigin(product.countryOfOrigin).name })} {countryOfOrigin(product.countryOfOrigin).flag}</span>
        )}
        {product.wholesaleEnabled && <span className="ppc__meta">{tiers ? elakayPluralText(moq, ["h_ppc_tiers_one", "Wholesale from {value0} unit · tier pricing"], ["h_ppc_tiers", "Wholesale from {value0} units · tier pricing"], { value0: moq }) : elakayPluralText(moq, ["h_ppc_from_one", "Wholesale from {value0} unit"], ["h_ppc_from", "Wholesale from {value0} units"], { value0: moq })}</span>}
        <span className={soldOut ? "ppc__meta ppc__meta--out" : "ppc__meta"}>{soldOut ? elakayAutoText("h_ppc_out", "Out of stock") : elakayAutoText("h_ppc_stock", "{value0} in stock", { value0: product.stock })}</span>
      </span>
    </Link>
  );
};

// Primary action, same rules as the Provider storefront cards (StorefrontProductCard): Add to My Shop
// when the product is offered for dropshipping and not yet in the Seller's shop, else Buy Wholesale,
// else nothing (the card itself opens the product page). "In My Shop" is shown as a state, not an action.
export const historyCardAction = (product) => {
  const inShop = Boolean(product.sellerLink && product.sellerLink.status !== "removed");
  if (product.dropshippingEnabled && !inShop) return { key: "add", to: `/seller/provider-products/add/${product._id}`, label: elakayAutoText("h_psf_add_shop", "Add to My Shop"), Icon: MdAddBusiness };
  if (product.wholesaleEnabled) return { key: "wholesale", to: `/seller/provider-products/${product._id}/buy`, label: elakayAutoText("h_psf_buy_wholesale", "Buy Wholesale"), Icon: FaBoxes };
  return null;
};

const HistoryCard = ({ product }) => {
  const infoSource = React.useContext(DropshippingLanguageContext);
  const presentation = getCardGlobalPresentation(infoSource?.allSettings);
  const [broken, setBroken] = React.useState(false);
  const [logoBroken, setLogoBroken] = React.useState(false);
  const url = `/seller/provider-products/${product._id}`;
  const inShop = Boolean(product.sellerLink && product.sellerLink.status !== "removed");
  const action = historyCardAction(product);
  const shopName = providerShopDisplayName(product.shop, product.provider) || elakayAutoText("h_ppc_provider", "Provider");
  const logo = product.shop?.avatar;
  const image = product.images?.[0];
  return (
    <article className={`product-card elakay-grid-card pphc ${presentation.className || ""}`} style={{ ...presentation.style, ...presentation.variables }} data-product-id={product._id}>
      <Link to={url} className="pphc__media" aria-label={product.name}>
        {image && !broken ? <ElakayImage preset="product-card" src={toImageUrl(image)} alt={product.name || ""} loading="lazy" onError={() => setBroken(true)} /> : <span className="pphc__noimg" aria-hidden="true" />}
        {inShop && <span className="pphc__inshop"><FaCheck aria-hidden="true" /> {elakayAutoText("h_ppc_inshop", "In My Shop")}</span>}
      </Link>
      <div className="pphc__body">
        <GlobalRatingBadge ratings={product.ratings} size="14px" />
        <Link to={url} className="pphc__name" title={product.name}>{product.name}</Link>
        <p className="pphc__price">{moneyOf(product.basePrice, product.currency)}</p>
        <div className="pphc__footer">
          <Link to={providerStorefrontPath(product.providerId, null, resolveProductShop({ product })?._id)} className="pphc__provider" title={shopName}>
            {logo && !logoBroken
              ? <ElakayImage preset="avatar-tiny" className="pphc__plogo" src={toImageUrl(logo)} alt="" loading="lazy" onError={() => setLogoBroken(true)} />
              : <span className="pphc__plogo pphc__plogo--ph" aria-hidden="true">{providerShopMonogram(product.shop, product.provider)}</span>}
            <span className="pphc__pname">{shopName}</span>
          </Link>
          {action && (
            <Link to={action.to} className="add-to-cart-btn default-style pphc__action" aria-label={`${action.label}: ${product.name}`} title={action.label}>
              <action.Icon aria-hidden="true" />
            </Link>
          )}
        </div>
      </div>
    </article>
  );
};

// Same silhouette as HistoryCard: image, rating, title, price, provider.
export const ProviderProductCardSkeleton = () => (
  <div className="product-card elakay-grid-card pphc pphc--skeleton" aria-hidden="true">
    <div className="pphc__media sph-sk" />
    <div className="pphc__body">
      <span className="sph-sk sph-sk--line sph-sk--short" />
      <span className="sph-sk sph-sk--line" />
      <span className="sph-sk sph-sk--line sph-sk--price" />
      <span className="sph-sk sph-sk--line sph-sk--short" />
    </div>
  </div>
);

export default ProviderProductCard;
